<template>
    <div class="bsa-summary">
        <div v-for="card in cards" :key="card.key" class="bsa-summary-card">
            <span class="bsa-summary-icon" :class="card.tone">
                <span :key="card.icon"><i :class="card.icon"></i></span>
            </span>
            <div class="bsa-summary-text">
                <span class="bsa-summary-label">{{ card.label }}</span>
                <span v-if="loading" class="bsa-summary-skeleton"></span>
                <span v-else class="bsa-summary-value" :class="card.valueClass">{{ card.value }}</span>
                <span v-if="!loading && card.caption" class="bsa-summary-caption">{{ card.caption }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'

const props = defineProps({
    assetTotal: { type: Number, default: 0 },
    liabilityTotal: { type: Number, default: 0 },
    equityTotal: { type: Number, default: 0 },
    decimals: { type: [Number, String], default: 2 },
    asOf: { type: String, default: '' },
    loading: { type: Boolean, default: false }
})

const { commaFormat } = figureFormatter()

const difference = computed(() => props.assetTotal - props.liabilityTotal - props.equityTotal)
const isBalanced = computed(() => Math.abs(difference.value) < 0.01)
const caption = computed(() => props.asOf ? `As of ${props.asOf}` : '')

const amountCard = (key, label, icon, tone, value) => ({
    key,
    label,
    icon,
    tone,
    value: commaFormat(value, props.decimals),
    valueClass: value < 0 ? 'is-negative' : '',
    caption: caption.value
})

const balanceCard = computed(() => ({
    key: 'balance',
    label: 'Balance Check',
    icon: isBalanced.value ? 'fa-solid fa-circle-check' : 'fa-solid fa-triangle-exclamation',
    tone: isBalanced.value ? 'is-success' : 'is-danger',
    value: isBalanced.value ? 'Balanced' : `Off by ${commaFormat(difference.value, props.decimals)}`,
    valueClass: isBalanced.value ? 'is-success-text' : 'is-negative',
    caption: 'Assets = Liabilities + Equity'
}))

const cards = computed(() => [
    amountCard('asset', 'Total Assets', 'fa-solid fa-building-columns', 'is-primary', props.assetTotal),
    amountCard('liability', 'Total Liabilities', 'fa-solid fa-file-invoice-dollar', 'is-warning', props.liabilityTotal),
    amountCard('equity', 'Total Equity', 'fa-solid fa-scale-balanced', 'is-info', props.equityTotal),
    balanceCard.value
])
</script>

<style scoped>
.bsa-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}
.bsa-summary-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #eaecf0;
    border-radius: 10px;
    background: #fff;
}
.bsa-summary-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 16px;
}
.bsa-summary-icon.is-primary {
    background: #f4f3ff;
    color: #7367f0;
}
.bsa-summary-icon.is-warning {
    background: #fffaeb;
    color: #dc6803;
}
.bsa-summary-icon.is-info {
    background: #eff8ff;
    color: #1570ef;
}
.bsa-summary-icon.is-success {
    background: #ecfdf3;
    color: #039855;
}
.bsa-summary-icon.is-danger {
    background: #fef3f2;
    color: #d92d20;
}
.bsa-summary-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.bsa-summary-label {
    font-size: 12px;
    font-weight: 500;
    color: #667085;
}
.bsa-summary-value {
    font-size: 19px;
    font-weight: 600;
    line-height: 1.3;
    color: #101828;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.bsa-summary-value.is-negative {
    color: #d92d20;
}
.bsa-summary-value.is-success-text {
    color: #039855;
}
.bsa-summary-caption {
    font-size: 11px;
    color: #98a2b3;
}
.bsa-summary-skeleton {
    display: block;
    width: 110px;
    height: 22px;
    margin-top: 2px;
    border-radius: 6px;
    background: linear-gradient(90deg, #f2f4f7 25%, #e4e7ec 50%, #f2f4f7 75%);
    background-size: 200% 100%;
    animation: bsa-shimmer 1.2s infinite;
}
@keyframes bsa-shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
